import React, { FC, useCallback, useEffect, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import { Divider, List, ListItem, ListItemText, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SubHeading } from '../common/PageHeadings/PageHeadings';
import Card from '../common/Card/Card';
import { WarrantyRegistrationAWS } from '../../@types/AWS';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import moment from 'moment';


interface DealerWarrantyRegistrationsProps { dealerId?: string }

const tableHeaders = ['owner', 'vehicle', 'serial', 'date created']

const DealerWarrantyRegistrations: FC<DealerWarrantyRegistrationsProps> = ({ dealerId }) => {

  const { user } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);

  const [warrantyRegistrations, setWarrantyRegistrations] = useState<WarrantyRegistrationAWS[]>([]);

  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const fetchWarrantyRegistrations = useCallback(async () => {

    try {

      setLoading(true);

      const queryParams = { dealerId: dealerId ?? user?.companyAttributes?.dealerId };

      const response = await axios.get('/warranties/cart/registrations', { params: queryParams });

      setWarrantyRegistrations(response.data.data);

    } catch (error: any) {

      console.log(error);

      toast.error('Error loading warranty registrations')

    } finally {

      setLoading(false);

    }

  }, [user, dealerId]);

  useEffect(() => { fetchWarrantyRegistrations() }, [fetchWarrantyRegistrations]);

  return (

    <Stack spacing={2}>

      <Stack sx={{ marginBottom: '16px' }}>

        <SubHeading>Warranty Registrations</SubHeading>

      </Stack>

      <Card>

        {!loading ?

          (warrantyRegistrations.length ?

            (!isTablet ?

              (

                <Stack direction="column" spacing={2} alignItems='flex-end'>

                  <TableContainer>

                    <Table>

                      <TableHead>

                        <TableRow>

                          {tableHeaders.map((header, index) => (

                            <TableCell key={index}
                              sx={{
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderTop: 'none',
                                textTransform: 'uppercase',
                                ...((header === 'date created' || header === 'serial') && {
                                  textAlign: 'center'
                                }),
                              }}>

                              {header}

                            </TableCell>

                          ))}

                        </TableRow>

                      </TableHead>

                      <TableBody>

                        {warrantyRegistrations.map((warrantyRegistration, index) => (

                          <TableRow key={index}
                            sx={{
                              '.MuiTableCell-root': {
                                border: 'none',
                                paddingTop: '12px',
                                paddingBottom: '12px'
                              },
                            }}>

                            <TableCell>

                              {warrantyRegistration?.ownerInformation?.company ?? `${warrantyRegistration?.ownerInformation?.firstName} ${warrantyRegistration?.ownerInformation?.lastName}`}

                            </TableCell>

                            <TableCell>

                              {warrantyRegistration?.vehicleInformation?.vehicleYear} {warrantyRegistration?.vehicleInformation?.vehicleModel}

                            </TableCell>

                            <TableCell sx={{ textAlign: 'center' }}>

                              {warrantyRegistration?.vehicleInformation?.vehicleSerialNumber}

                            </TableCell>

                            <TableCell sx={{ textAlign: 'center' }}>

                              {moment(new Date(warrantyRegistration?.dateCreated ?? '')).format('MM/DD/YYYY')}

                            </TableCell>

                          </TableRow>

                        ))}

                      </TableBody>

                    </Table>

                  </TableContainer>

                </Stack>

              )


              : (

                <Stack direction="column" spacing={2} divider={<Divider />}>

                  {warrantyRegistrations.map((warrantyRegistration, index) => (

                    <List key={index}
                      sx={{
                        '.MuiListItem-root': {
                          paddingLeft: 0,
                          paddingRight: 0,
                          '.MuiListItemText-root:last-of-type': {
                            textAlign: 'right',
                            maxWidth: '50%'
                          }
                        }
                      }}>

                      <ListItem>

                        <ListItemText>Owner</ListItemText>

                        <ListItemText>{warrantyRegistration?.ownerInformation?.company ?? `${warrantyRegistration?.ownerInformation?.firstName} ${warrantyRegistration?.ownerInformation?.lastName}`}</ListItemText>

                      </ListItem>

                      <ListItem>

                        <ListItemText>Vehicle</ListItemText>

                        <ListItemText>{warrantyRegistration?.vehicleInformation?.vehicleYear} {warrantyRegistration?.vehicleInformation?.vehicleModel}</ListItemText>

                      </ListItem>

                      <ListItem>

                        <ListItemText>Serial</ListItemText>

                        <ListItemText>{warrantyRegistration?.vehicleInformation?.vehicleSerialNumber}</ListItemText>

                      </ListItem>

                      <ListItem>

                        <ListItemText>Date Created</ListItemText>

                        <ListItemText>{moment(new Date(warrantyRegistration?.dateCreated ?? '')).format('MM/DD/YYYY')}</ListItemText>

                      </ListItem>

                    </List>

                  ))}

                </Stack>

              )

            )

            : (
              <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={{ minHeight: '150px' }}
                spacing={2}>

                <Typography>

                  Unable to locate any warranty registrations Dealer ID# {dealerId ?? user?.companyAttributes?.dealerId}

                </Typography>

              </Stack>)

          )

          : (<WarrantyRegistrationsLoading />)

        }

      </Card>

    </Stack>

  );

}

const WarrantyRegistrationsLoading = () => {

  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const displayCount = 4;

  return (

    (!isTablet

      ? (

        <TableContainer>

          <Table>

            <TableHead>

              <TableRow>

                {tableHeaders.map((header, index) => (

                  <TableCell
                    key={index}
                    sx={{
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTop: 'none',
                      textTransform: 'uppercase',
                      ...((header === 'date created' || header === 'serial') && {
                        textAlign: 'center'
                      })
                    }}>

                    {header}

                  </TableCell>

                ))}

              </TableRow>

            </TableHead>

            <TableBody>

              {Array(displayCount).fill(null).map((value, index) => (

                <TableRow
                  key={index}
                  sx={{
                    '.MuiTableCell-root': {
                      border: 'none',
                      paddingTop: '12px',
                      paddingBottom: '12px'
                    }
                  }}>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                </TableRow>

              ))}

            </TableBody>

          </Table>

        </TableContainer>

      )

      : (

        <Stack direction="column" spacing={2} divider={<Divider />}>

          {Array(displayCount).fill(null).map((value, index) => (

            <List key={index}
              sx={{
                '.MuiListItem-root': {
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              }}>

              <ListItem>

                <ListItemText>Owner</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Vehicle</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Serial</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Date Created</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

            </List>

          ))}

        </Stack>

      )

    )

  )

}

export default DealerWarrantyRegistrations;
